import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Input, Select, toast } from '../../components/common';
import { budgetApi, departmentApi } from '../../services/api';
import './BudgetForm.css';

export default function BudgetForm({ budget, fiscalYearId, onSuccess, onCancel }) {
    const isEditing = !!budget;

    const [form, setForm] = useState({
        departmentId: '',
        fiscalYearId: fiscalYearId || '',
        totalAmount: '',
        lines: []
    });
    const [errors, setErrors] = useState({});

    // Fetch departments from API
    const { data: departments = [] } = useQuery({
        queryKey: ['departments'],
        queryFn: async () => {
            try {
                const res = await departmentApi.list();
                return res.data?.data || res.data || [];
            } catch {
                // Fallback if API fails
                return [];
            }
        }
    });

    useEffect(() => {
        if (budget) {
            setForm({
                departmentId: budget.departmentId || '',
                fiscalYearId: budget.fiscalYearId || fiscalYearId || '',
                totalAmount: budget.totalAmount?.toString() || '',
                lines: budget.lines || []
            });
        }
    }, [budget, fiscalYearId]);

    const mutation = useMutation({
        mutationFn: (data) => isEditing
            ? budgetApi.update(budget.id, data)
            : budgetApi.create(data),
        onSuccess: () => {
            toast.success(`Budget ${isEditing ? 'updated' : 'created'} successfully`);
            onSuccess?.();
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to save budget');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const addLine = () => {
        setForm(prev => ({
            ...prev,
            lines: [...prev.lines, { categoryName: '', allocatedAmount: '' }]
        }));
    };

    const updateLine = (index, field, value) => {
        setForm(prev => ({
            ...prev,
            lines: prev.lines.map((line, i) =>
                i === index ? { ...line, [field]: value } : line
            )
        }));
    };

    const removeLine = (index) => {
        setForm(prev => ({
            ...prev,
            lines: prev.lines.filter((_, i) => i !== index)
        }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.departmentId) newErrors.departmentId = 'Department is required';
        if (!form.totalAmount) newErrors.totalAmount = 'Total amount is required';
        if (!form.fiscalYearId) newErrors.fiscalYearId = 'Fiscal year is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;

        mutation.mutate({
            departmentId: form.departmentId, // Keep as UUID string
            fiscalYearId: form.fiscalYearId, // Keep as UUID string
            totalAmount: parseFloat(form.totalAmount),
            lines: form.lines.map(line => ({
                categoryName: line.categoryName,
                allocatedAmount: parseFloat(line.allocatedAmount) || 0
            }))
        });
    };

    const deptOptions = Array.isArray(departments) ? departments.map(d => ({ value: d.id, label: d.name })) : [];

    return (
        <form onSubmit={handleSubmit} className="budget-form">
            <div className="form-grid">
                <Select
                    name="departmentId"
                    label="Department"
                    value={form.departmentId}
                    onChange={handleChange}
                    options={deptOptions}
                    error={errors.departmentId}
                    disabled={isEditing}
                    required
                />
                <Input
                    name="totalAmount"
                    type="number"
                    label="Total Budget Amount"
                    value={form.totalAmount}
                    onChange={handleChange}
                    error={errors.totalAmount}
                    required
                />
            </div>

            <div className="budget-lines">
                <div className="lines-header">
                    <h4>Budget Lines (Optional)</h4>
                    <Button type="button" variant="secondary" size="sm" icon={Plus} onClick={addLine}>
                        Add Line
                    </Button>
                </div>

                {form.lines.map((line, index) => (
                    <div key={index} className="line-row">
                        <Input
                            placeholder="Category name"
                            value={line.categoryName}
                            onChange={(e) => updateLine(index, 'categoryName', e.target.value)}
                        />
                        <Input
                            type="number"
                            placeholder="Amount"
                            value={line.allocatedAmount}
                            onChange={(e) => updateLine(index, 'allocatedAmount', e.target.value)}
                        />
                        <button type="button" className="remove-line" onClick={() => removeLine(index)}>
                            <Trash2 size={16} />
                        </button>
                    </div>
                ))}
            </div>

            <div className="form-actions">
                <Button variant="secondary" type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" loading={mutation.isPending}>
                    {isEditing ? 'Update Budget' : 'Create Budget'}
                </Button>
            </div>
        </form>
    );
}

BudgetForm.propTypes = {
    budget: PropTypes.object,
    fiscalYearId: PropTypes.number,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func
};
